import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import {
  listenAdminChats,
  listenMessagesByChatId,
} from "../../services/chatService";

const formatDateTime = (value) => {
  if (!value) return "-";

  let date;
  if (typeof value === "number") {
    date = new Date(value);
  } else if (typeof value === "string") {
    const num = Number(value);
    date = !isNaN(num) && /^\d+$/.test(value) ? new Date(num) : new Date(value);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTime = (value) => {
  if (!value) return "";

  const date = new Date(Number(value));
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getMemberName = (member) => {
  if (!member) return "Unknown Member";

  return member.name || member.primary_id || member.profile_id || member.id || "Unknown Member";
};

const getMemberInitial = (member) => {
  const label = getMemberName(member).trim();
  return label ? label.charAt(0).toUpperCase() : "?";
};

function MemberAvatar({ member }) {
  if (member?.image) {
    return (
      <img
        src={member.image}
        alt={getMemberName(member)}
        style={styles.avatarImage}
      />
    );
  }

  return <div style={styles.avatarFallback}>{getMemberInitial(member)}</div>;
}

function MessageContent({ message }) {
  const isImage =
    message?.type === "image" && /^https?:\/\//i.test(message?.msg || "");

  if (isImage) {
    return (
      <a href={message.msg} target="_blank" rel="noreferrer">
        <img src={message.msg} alt="Chat attachment" style={styles.messageImage} />
      </a>
    );
  }

  return (
    <span style={styles.messageText}>{message?.msg || "Attachment"}</span>
  );
}

export default function ReportedChat() {
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");

  const [activeTab, setActiveTab] = useState("reports"); // "reports" | "chats"
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");

  // Reports state
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [reportsLoading, setReportsLoading] = useState(true);

  // Chats state
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState("");
  const [messages, setMessages] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const bottomRef = useRef(null);

  // Fetch reports from backend API
  const fetchReports = useCallback(async () => {
    try {
      setReportsLoading(true);
      const res = await fetch(`${API_URL}/api/admin/report-chat-list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();
      if (result.success && Array.isArray(result.data)) {
        setReports(result.data);
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error("Fetch Reports Error:", error);
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Listen to live chats from Firebase
  useEffect(() => {
    const unsubscribe = listenAdminChats((items) => {
      setChats(items);
      setListLoading(false);
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  // Filtered reports
  const filteredReports = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) {
      return reports;
    }

    return reports.filter((item) => {
      const reporter = item.report_by_member_id || {};
      const reported = item.reported_partner_id || {};
      const reporterMatch = [reporter.name, reporter.primary_id, reporter.profile_id, reporter.email, reporter.mobile]
        .some((val) => String(val || "").toLowerCase().includes(keyword));
      const reportedMatch = [reported.name, reported.primary_id, reported.profile_id, reported.email, reported.mobile]
        .some((val) => String(val || "").toLowerCase().includes(keyword));
      const commentMatch = String(item.comment || "").toLowerCase().includes(keyword);

      return reporterMatch || reportedMatch || commentMatch;
    });
  }, [reports, searchText]);

  // Filtered chats
  const filteredChats = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    if (!keyword) {
      return chats;
    }

    return chats.filter((chat) => {
      const userMatch = chat.users.some((member) =>
        [member.name, member.primary_id, member.id].some((value) =>
          String(value || "").toLowerCase().includes(keyword)
        )
      );
      const chatMatch = [chat.lastMessage, chat.chatId].some((value) =>
        String(value || "").toLowerCase().includes(keyword)
      );

      return userMatch || chatMatch;
    });
  }, [chats, searchText]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset page to 1 when tab or search keyword changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, searchText]);

  const currentItems = activeTab === "reports" ? filteredReports : filteredChats;
  const totalItems = currentItems.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const paginatedReports = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredReports.slice(start, start + pageSize);
  }, [filteredReports, page, pageSize]);

  const paginatedChats = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredChats.slice(start, start + pageSize);
  }, [filteredChats, page, pageSize]);

  const paginationRange = useMemo(() => {
    const delta = 2;
    const range = [];
    for (let i = Math.max(1, page - delta); i <= Math.min(totalPages, page + delta); i++) {
      range.push(i);
    }
    return range;
  }, [page, totalPages]);

  // Auto select report when report list changes
  useEffect(() => {
    if (activeTab === "reports") {
      if (!filteredReports.length) {
        setSelectedReportId("");
        return;
      }
      const exists = filteredReports.some((r) => r._id === selectedReportId);
      if (!exists) {
        setSelectedReportId(filteredReports[0]._id);
      }
    }
  }, [activeTab, filteredReports, selectedReportId]);

  // Auto select chat when chats list changes
  useEffect(() => {
    if (activeTab === "chats") {
      if (!filteredChats.length) {
        setSelectedChatId("");
        return;
      }
      const exists = filteredChats.some((chat) => chat.chatId === selectedChatId);
      if (!exists) {
        setSelectedChatId(filteredChats[0].chatId);
      }
    }
  }, [activeTab, filteredChats, selectedChatId]);

  // Selected report object
  const selectedReport = useMemo(
    () => reports.find((r) => r._id === selectedReportId) || null,
    [reports, selectedReportId]
  );

  // When a report is selected, check if a chat conversation exists in Firebase between reporter and reported user
  useEffect(() => {
    if (activeTab === "reports" && selectedReport) {
      const reporter = selectedReport.report_by_member_id;
      const reported = selectedReport.reported_partner_id;

      if (!reporter || !reported) {
        setSelectedChatId("");
        return;
      }

      const p1 = (reporter.primary_id || reporter.profile_id || reporter._id || "").toString().toLowerCase();
      const p2 = (reported.primary_id || reported.profile_id || reported._id || "").toString().toLowerCase();

      // Find in chats list
      const matchingChat = chats.find((c) => {
        const cId = (c.chatId || "").toString().toLowerCase();
        if (p1 && p2 && cId.includes(p1) && cId.includes(p2)) return true;

        const uIds = (c.users || []).map((u) => (u.primary_id || u.id || "").toString().toLowerCase());
        return p1 && p2 && uIds.includes(p1) && uIds.includes(p2);
      });

      if (matchingChat) {
        setSelectedChatId(matchingChat.chatId);
      } else {
        setSelectedChatId("");
      }
    }
  }, [activeTab, selectedReport, chats]);

  // Selected chat object (either from activeTab 'chats' or matched in 'reports')
  const selectedChat = useMemo(
    () => chats.find((chat) => chat.chatId === selectedChatId) || null,
    [chats, selectedChatId]
  );

  // Listen to messages for selectedChatId
  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      setMessageLoading(false);
      return;
    }

    setMessageLoading(true);

    const unsubscribe = listenMessagesByChatId(selectedChatId, (items) => {
      setMessages(items);
      setMessageLoading(false);
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [selectedChatId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, selectedChatId]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchText(searchInput);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearchText("");
  };

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Customer Support - Reported Members" />

        <div className="right_col" role="main">
          <div className="x_panel">
            <div className="x_title">
              <h2>Reported Members & Chats</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>
              </ul>
              <div className="clearfix" />
            </div>

            <div className="x_content">
              {/* Tab Switcher */}
              <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn"
                  style={{
                    borderRadius: "24px",
                    fontWeight: 600,
                    padding: "8px 22px",
                    backgroundColor: activeTab === "reports" ? "#b71c5a" : "#eef1f5",
                    color: activeTab === "reports" ? "#ffffff" : "#495057",
                    border: "none",
                    boxShadow: activeTab === "reports" ? "0 4px 12px rgba(183, 28, 90, 0.3)" : "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => {
                    setActiveTab("reports");
                    setSearchInput("");
                    setSearchText("");
                  }}
                >
                  <i className="fa fa-flag" style={{ marginRight: "8px" }} />
                  Reported Members ({reports.length})
                </button>

                <button
                  type="button"
                  className="btn"
                  style={{
                    borderRadius: "24px",
                    fontWeight: 600,
                    padding: "8px 22px",
                    backgroundColor: activeTab === "chats" ? "#b71c5a" : "#eef1f5",
                    color: activeTab === "chats" ? "#ffffff" : "#495057",
                    border: "none",
                    boxShadow: activeTab === "chats" ? "0 4px 12px rgba(183, 28, 90, 0.3)" : "none",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => {
                    setActiveTab("chats");
                    setSearchInput("");
                    setSearchText("");
                  }}
                >
                  <i className="fa fa-comments" style={{ marginRight: "8px" }} />
                  All Member Chats ({chats.length})
                </button>
              </div>

              {/* Search Filter */}
              <div className="filtercont" style={{ marginBottom: "20px" }}>
                <form onSubmit={handleSearchSubmit}>
                  <div className="row">
                    <div className="col-lg-5 col-md-6 col-sm-12">
                      <label>
                        {activeTab === "reports"
                          ? "Search by Name, Profile ID, or Report Reason"
                          : "Search by Member Name, ID, or Chat text"}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={
                          activeTab === "reports"
                            ? "e.g. Amit, #VS20210, spam..."
                            : "Name, profile ID or chat text"
                        }
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                      />
                    </div>
                    <div className="col-lg-4 col-md-6 col-sm-12">
                      <label>&nbsp;</label>
                      <div style={styles.filterActions}>
                        <button type="submit" className="btn btn-default submit sitebtn">
                          Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-dark submit text-white sitebtn"
                          onClick={handleReset}
                        >
                          Reset
                        </button>
                        {activeTab === "reports" && (
                          <button
                            type="button"
                            className="btn btn-info text-white sitebtn"
                            onClick={fetchReports}
                            title="Refresh Reports"
                          >
                            <i className="fa fa-undo" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-12 col-sm-12" style={styles.summaryBox}>
                      <label>
                        {activeTab === "reports" ? "Total Reports" : "Total Chats"}
                      </label>
                      <div className="form-control" style={styles.summaryValue}>
                        {activeTab === "reports" ? filteredReports.length : filteredChats.length}
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className="row" style={styles.contentRow}>
                {/* Left Table Panel */}
                <div className="col-lg-7 col-md-12 col-sm-12">
                  <div
                    className="table-responsive nolengthtable"
                    style={styles.memberListScroll}
                  >
                    {activeTab === "reports" ? (
                      /* REPORTED MEMBERS TABLE */
                      <table
                        className="table table-striped table-bordered bulk_action mytable"
                        style={{ width: "100%", marginBottom: 0 }}
                        cellPadding={0}
                        cellSpacing={0}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: 45, textAlign: "center" }}>#</th>
                            <th style={{ width: 140 }}>Report Date</th>
                            <th>Reported By</th>
                            <th>Reported Member</th>
                            <th>Reason / Message</th>
                            <th style={{ width: 85, textAlign: "center" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportsLoading ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                Loading reports...
                              </td>
                            </tr>
                          ) : paginatedReports.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                No member reports found.
                              </td>
                            </tr>
                          ) : (
                            paginatedReports.map((report, index) => {
                              const reporter = report.report_by_member_id || {};
                              const reported = report.reported_partner_id || {};
                              const isSelected = report._id === selectedReportId;

                              return (
                                <tr
                                  key={report._id}
                                  style={isSelected ? styles.activeRow : undefined}
                                >
                                  <td style={{ textAlign: "center", fontWeight: 600, color: "#6c757d" }}>
                                    {(page - 1) * pageSize + index + 1}
                                  </td>
                                  <td>
                                    <div style={{ fontSize: "12px", fontWeight: 600 }}>
                                      {formatDateTime(report.createdAt)}
                                    </div>
                                    <span
                                      className="badge"
                                      style={{
                                        backgroundColor: "#fce3ec",
                                        color: "#b71c5a",
                                        fontSize: "10px",
                                        marginTop: "4px",
                                      }}
                                    >
                                      {report.report_type || "Profile Report"}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="userdet">
                                      <div>
                                        <i className="far fa-user" /> {getMemberName(reporter)}
                                      </div>
                                      <div style={{ color: "#b71c5a", fontWeight: 600 }}>
                                        #{reporter.primary_id || reporter.profile_id || reporter._id?.slice(-6) || "-"}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="userdet">
                                      <div>
                                        <i className="far fa-user text-danger" /> {getMemberName(reported)}
                                      </div>
                                      <div style={{ color: "#d9534f", fontWeight: 600 }}>
                                        #{reported.primary_id || reported.profile_id || reported._id?.slice(-6) || "-"}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div
                                      style={{
                                        maxWidth: 190,
                                        fontSize: "12px",
                                        color: "#333",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        backgroundColor: "#fff9e6",
                                        padding: "6px 10px",
                                        borderRadius: "6px",
                                        borderLeft: "3px solid #ffc107",
                                      }}
                                      title={report.comment}
                                    >
                                      {report.comment || "No comment provided"}
                                    </div>
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    <button
                                      type="button"
                                      className="sitebtn text-white text-center px-2 py-1"
                                      style={styles.viewButton}
                                      onClick={() => setSelectedReportId(report._id)}
                                      title="View Details"
                                    >
                                      <i className="fa fa-eye" aria-hidden="true" /> View
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    ) : (
                      /* ALL CHATS TABLE */
                      <table
                        className="table table-striped table-bordered bulk_action mytable"
                        style={{ width: "100%", marginBottom: 0 }}
                        cellPadding={0}
                        cellSpacing={0}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: 45, textAlign: "center" }}>#</th>
                            <th style={{ width: 140 }}>Last Activity</th>
                            <th>Member One</th>
                            <th>Member Two</th>
                            <th>Last Message</th>
                            <th style={{ width: 95, textAlign: "center" }}>View Chat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listLoading ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                Loading chats...
                              </td>
                            </tr>
                          ) : paginatedChats.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center py-4">
                                No chat conversations found.
                              </td>
                            </tr>
                          ) : (
                            paginatedChats.map((chat, index) => {
                              const firstMember = chat.users[0];
                              const secondMember = chat.users[1];
                              const isActive = chat.chatId === selectedChatId;

                              return (
                                <tr
                                  key={chat.chatId}
                                  style={isActive ? styles.activeRow : undefined}
                                >
                                  <td style={{ textAlign: "center", fontWeight: 600, color: "#6c757d" }}>
                                    {(page - 1) * pageSize + index + 1}
                                  </td>
                                  <td>{formatDateTime(chat.lastMessageTime || chat.createdAt)}</td>
                                  <td>
                                    <div className="userdet">
                                      <div>
                                        <i className="far fa-user" /> {getMemberName(firstMember)}
                                      </div>
                                      <div>#{firstMember?.primary_id || firstMember?.id || "-"}</div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="userdet">
                                      <div>
                                        <i className="far fa-user" /> {getMemberName(secondMember)}
                                      </div>
                                      <div>#{secondMember?.primary_id || secondMember?.id || "-"}</div>
                                    </div>
                                  </td>
                                  <td>
                                    <div style={styles.previewText}>
                                      {chat.lastMessage || "No messages yet"}
                                    </div>
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    <button
                                      type="button"
                                      className="sitebtn text-white text-center px-2 py-1"
                                      style={styles.viewButton}
                                      onClick={() => setSelectedChatId(chat.chatId)}
                                    >
                                      <i className="fa fa-eye" aria-hidden="true" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  <div
                    className="d-flex justify-content-between align-items-center mt-3 flex-wrap"
                    style={{ gap: "10px", padding: "4px 2px" }}
                  >
                    <div>
                      <label style={{ fontSize: "13px", color: "#495057", marginBottom: 0 }}>
                        Show{" "}
                        <select
                          value={pageSize}
                          onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setPage(1);
                          }}
                          className="form-select form-select-sm d-inline-block w-auto"
                          style={{
                            margin: "0 6px",
                            fontSize: "12px",
                            height: "30px",
                            padding: "2px 24px 2px 8px",
                            display: "inline-block",
                            width: "auto",
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>{" "}
                        entries
                      </label>
                    </div>

                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      {totalItems > 0
                        ? `Showing ${(page - 1) * pageSize + 1} to ${Math.min(
                            page * pageSize,
                            totalItems
                          )} of ${totalItems} entries`
                        : "0 entries"}
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: "4px" }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          backgroundColor: "#f0f2f5",
                          color: "#333",
                          border: "1px solid #ced4da",
                          fontSize: "12px",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          cursor: page === 1 ? "not-allowed" : "pointer",
                          opacity: page === 1 ? 0.6 : 1,
                        }}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        Previous
                      </button>

                      {paginationRange.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPage(p)}
                          style={{
                            backgroundColor: page === p ? "#b71c5a" : "#ffffff",
                            borderColor: "#b71c5a",
                            color: page === p ? "#ffffff" : "#b71c5a",
                            border: "1px solid #b71c5a",
                            fontSize: "12px",
                            padding: "4px 10px",
                            borderRadius: "4px",
                            fontWeight: page === p ? "700" : "500",
                            minWidth: "30px",
                          }}
                          className="btn btn-sm"
                        >
                          {p}
                        </button>
                      ))}

                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                          backgroundColor: "#f0f2f5",
                          color: "#333",
                          border: "1px solid #ced4da",
                          fontSize: "12px",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          cursor: page === totalPages || totalPages === 0 ? "not-allowed" : "pointer",
                          opacity: page === totalPages || totalPages === 0 ? 0.6 : 1,
                        }}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages || totalPages === 0}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Viewer Panel */}
                <div className="col-lg-5 col-md-12 col-sm-12">
                  <div style={styles.viewerCard}>
                    {activeTab === "reports" ? (
                      /* REPORT DETAILS + CONVERSATION VIEWER */
                      !selectedReport ? (
                        <div style={styles.emptyState}>
                          Select a report from the table to view details.
                        </div>
                      ) : (
                        <>
                          <div style={styles.viewerHeader}>
                            <div style={styles.viewerTitleRow}>
                              <h3 style={styles.viewerTitle}>Report Details</h3>
                              <span
                                style={{
                                  backgroundColor: "#fff0f5",
                                  border: "1px solid #f8c8dc",
                                  borderRadius: 8,
                                  color: "#b71c5a",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  padding: "4px 10px",
                                }}
                              >
                                {selectedReport.report_type || "Profile Report"}
                              </span>
                            </div>

                            {/* Two Members Card */}
                            <div style={styles.memberHeaderCard}>
                              {/* Reporter */}
                              <div style={styles.memberBox}>
                                <div style={{ fontSize: "11px", color: "#6c757d", marginBottom: "4px" }}>
                                  REPORTED BY:
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <MemberAvatar member={selectedReport.report_by_member_id} />
                                  <div>
                                    <div style={styles.memberName}>
                                      {getMemberName(selectedReport.report_by_member_id)}
                                    </div>
                                    <div style={styles.memberMeta}>
                                      #{selectedReport.report_by_member_id?.primary_id ||
                                        selectedReport.report_by_member_id?.profile_id ||
                                        "-"}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div style={{ alignSelf: "center", color: "#d9534f", fontSize: "18px" }}>
                                <i className="fa fa-arrow-right" />
                              </div>

                              {/* Reported */}
                              <div style={styles.memberBox}>
                                <div style={{ fontSize: "11px", color: "#d9534f", marginBottom: "4px" }}>
                                  REPORTED MEMBER:
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <MemberAvatar member={selectedReport.reported_partner_id} />
                                  <div>
                                    <div style={styles.memberName}>
                                      {getMemberName(selectedReport.reported_partner_id)}
                                    </div>
                                    <div style={styles.memberMeta}>
                                      #{selectedReport.reported_partner_id?.primary_id ||
                                        selectedReport.reported_partner_id?.profile_id ||
                                        "-"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Report Reason / Message Box */}
                            <div
                              style={{
                                marginTop: "14px",
                                backgroundColor: "#fff8f0",
                                border: "1px solid #ffd8a8",
                                borderRadius: "10px",
                                padding: "12px 16px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: "#d9480f",
                                  textTransform: "uppercase",
                                  marginBottom: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <i className="fa fa-exclamation-triangle" /> Report Reason / Message:
                              </div>
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: "#212529",
                                  fontWeight: 500,
                                  lineHeight: 1.5,
                                }}
                              >
                                {selectedReport.comment || "No comment provided"}
                              </div>
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#868e96",
                                  marginTop: "6px",
                                }}
                              >
                                Reported on: {formatDateTime(selectedReport.createdAt)}
                              </div>
                            </div>
                          </div>

                          {/* Chat Conversation Sub-section */}
                          <div style={{ padding: "0 18px 12px 18px" }}>
                            <div
                              style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#495057",
                                textTransform: "uppercase",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                marginBottom: "8px",
                              }}
                            >
                              <i className="fa fa-comments" />
                              {selectedChatId ? "Conversation History:" : "Chat History:"}
                            </div>
                          </div>

                          {selectedChatId ? (
                            <div style={styles.messagesContainer}>
                              {messageLoading ? (
                                <div style={styles.emptyState}>Loading messages...</div>
                              ) : messages.length === 0 ? (
                                <div style={styles.emptyState}>
                                  Chat room exists, but no messages sent yet.
                                </div>
                              ) : (
                                messages.map((message) => {
                                  const isReporter =
                                    String(message.fromId) ===
                                    String(selectedReport.report_by_member_id?.primary_id) ||
                                    String(message.fromId) ===
                                    String(selectedReport.report_by_member_id?._id);

                                  return (
                                    <div
                                      key={message.id}
                                      style={{
                                        ...styles.messageRow,
                                        justifyContent: isReporter ? "flex-end" : "flex-start",
                                      }}
                                    >
                                      <div
                                        style={{
                                          ...styles.messageBubble,
                                          ...(isReporter
                                            ? styles.secondaryMessageBubble
                                            : styles.primaryMessageBubble),
                                        }}
                                      >
                                        <div style={styles.messageSender}>
                                          {isReporter
                                            ? getMemberName(selectedReport.report_by_member_id)
                                            : getMemberName(selectedReport.reported_partner_id)}
                                        </div>
                                        <MessageContent message={message} />
                                        <div style={styles.messageFooter}>
                                          <span>{formatTime(message.sent)}</span>
                                          <span>{message.read ? "Seen" : "Delivered"}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                              <div ref={bottomRef} />
                            </div>
                          ) : (
                            <div
                              style={{
                                margin: "0 18px 18px 18px",
                                backgroundColor: "#f8f9fa",
                                border: "1px dashed #ced4da",
                                borderRadius: "10px",
                                padding: "24px 16px",
                                textAlign: "center",
                                color: "#6c757d",
                              }}
                            >
                              <i
                                className="fa fa-info-circle"
                                style={{ fontSize: "28px", color: "#adb5bd", marginBottom: "8px" }}
                              />
                              <div style={{ fontWeight: 600, fontSize: "14px", color: "#495057" }}>
                                No Chat Conversation Found
                              </div>
                              <div style={{ fontSize: "12px", marginTop: "4px" }}>
                                This member was reported directly from their profile page.
                              </div>
                            </div>
                          )}
                        </>
                      )
                    ) : (
                      /* ALL CHATS CONVERSATION VIEWER */
                      !selectedChat ? (
                        <div style={styles.emptyState}>
                          Select a chat from the list to view the conversation.
                        </div>
                      ) : (
                        <>
                          <div style={styles.viewerHeader}>
                            <div style={styles.viewerTitleRow}>
                              <h3 style={styles.viewerTitle}>Conversation View</h3>
                              <span
                                style={{
                                  backgroundColor: "#f0f4f8",
                                  borderRadius: 8,
                                  color: "#5b6b79",
                                  fontSize: 12,
                                  padding: "4px 8px",
                                }}
                              >
                                {selectedChat.chatId}
                              </span>
                            </div>

                            <div style={styles.memberHeaderCard}>
                              <div style={styles.memberBox}>
                                <MemberAvatar member={selectedChat.users[0]} />
                                <div>
                                  <div style={styles.memberName}>
                                    {getMemberName(selectedChat.users[0])}
                                  </div>
                                  <div style={styles.memberMeta}>
                                    #{selectedChat.users[0]?.primary_id ||
                                      selectedChat.users[0]?.id ||
                                      "-"}
                                  </div>
                                </div>
                              </div>

                              <div style={styles.memberBox}>
                                <MemberAvatar member={selectedChat.users[1]} />
                                <div>
                                  <div style={styles.memberName}>
                                    {getMemberName(selectedChat.users[1])}
                                  </div>
                                  <div style={styles.memberMeta}>
                                    #{selectedChat.users[1]?.primary_id ||
                                      selectedChat.users[1]?.id ||
                                      "-"}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div style={styles.lastActivityText}>
                              Last updated:{" "}
                              {formatDateTime(
                                selectedChat.lastMessageTime || selectedChat.createdAt
                              )}
                            </div>
                          </div>

                          <div style={styles.messagesContainer}>
                            {messageLoading ? (
                              <div style={styles.emptyState}>Loading messages...</div>
                            ) : messages.length === 0 ? (
                              <div style={styles.emptyState}>
                                No messages in this conversation yet.
                              </div>
                            ) : (
                              messages.map((message) => {
                                const isSecondary =
                                  String(message.fromId) ===
                                  String(selectedChat.users[1]?.id);

                                return (
                                  <div
                                    key={message.id}
                                    style={{
                                      ...styles.messageRow,
                                      justifyContent: isSecondary ? "flex-end" : "flex-start",
                                    }}
                                  >
                                    <div
                                      style={{
                                        ...styles.messageBubble,
                                        ...(isSecondary
                                          ? styles.secondaryMessageBubble
                                          : styles.primaryMessageBubble),
                                      }}
                                    >
                                      <div style={styles.messageSender}>
                                        {isSecondary
                                          ? getMemberName(selectedChat.users[1])
                                          : getMemberName(selectedChat.users[0])}
                                      </div>
                                      <MessageContent message={message} />
                                      <div style={styles.messageFooter}>
                                        <span>{formatTime(message.sent)}</span>
                                        <span>{message.read ? "Seen" : "Delivered"}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                            <div ref={bottomRef} />
                          </div>
                        </>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

const styles = {
  activeRow: {
    backgroundColor: "#fff0f5",
  },
  avatarFallback: {
    alignItems: "center",
    backgroundColor: "#b71c5a",
    borderRadius: "50%",
    color: "#ffffff",
    display: "flex",
    fontSize: 14,
    fontWeight: 700,
    height: 40,
    justifyContent: "center",
    minWidth: 40,
    width: 40,
  },
  avatarImage: {
    borderRadius: "50%",
    height: 40,
    minWidth: 40,
    objectFit: "cover",
    width: 40,
  },
  contentRow: {
    alignItems: "flex-start",
    rowGap: 24,
  },
  emptyState: {
    color: "#7c8ba0",
    fontSize: 14,
    padding: "48px 24px",
    textAlign: "center",
  },
  filterActions: {
    display: "flex",
    gap: 8,
  },
  lastActivityText: {
    color: "#7a8a9a",
    fontSize: 12,
    marginTop: 12,
  },
  memberBox: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    gap: 12,
  },
  memberHeaderCard: {
    backgroundColor: "#f9fbfe",
    border: "1px solid #e7ecf2",
    borderRadius: 12,
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    padding: 12,
    marginTop: 16,
  },
  memberListScroll: {
    border: "1px solid #e4e8ed",
    borderRadius: 12,
    height: "calc(100vh - 360px)",
    minHeight: 540,
    overflowY: "auto",
  },
  memberMeta: {
    color: "#6c757d",
    fontSize: 12,
  },
  memberName: {
    color: "#243447",
    fontSize: 14,
    fontWeight: 600,
  },
  messageBubble: {
    borderRadius: 16,
    boxShadow: "0 8px 20px rgba(26, 37, 48, 0.08)",
    maxWidth: "82%",
    padding: 14,
  },
  messageFooter: {
    color: "#6c757d",
    display: "flex",
    fontSize: 11,
    gap: 12,
    justifyContent: "space-between",
    marginTop: 10,
  },
  messageImage: {
    borderRadius: 12,
    display: "block",
    maxHeight: 240,
    maxWidth: "100%",
    objectFit: "cover",
  },
  messageRow: {
    display: "flex",
    marginBottom: 14,
  },
  messageSender: {
    color: "#2f4153",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 8,
  },
  messageText: {
    color: "#23313f",
    display: "block",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  messagesContainer: {
    backgroundColor: "#f5f7fb",
    borderTop: "1px solid #e7ebf0",
    height: "calc(100vh - 460px)",
    minHeight: 320,
    overflowY: "auto",
    padding: 18,
  },
  previewText: {
    maxWidth: 240,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  primaryMessageBubble: {
    backgroundColor: "#ffffff",
  },
  secondaryMessageBubble: {
    backgroundColor: "#fce3ec",
  },
  summaryBox: {
    alignSelf: "flex-end",
  },
  summaryValue: {
    backgroundColor: "#f8fafc",
    fontWeight: 600,
    textAlign: "center",
  },
  viewButton: {
    border: "none",
    display: "inline-block",
    fontSize: 12,
    textDecoration: "none",
  },
  viewerCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e4e8ed",
    borderRadius: 14,
    overflow: "hidden",
  },
  viewerHeader: {
    padding: 18,
  },
  viewerTitle: {
    color: "#1e2b38",
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
  },
  viewerTitleRow: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
};